import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "selectedList", "dropdown"]
  static values = {
    authors: Array,
    selected: Array
  }

  connect() {
    // Parse JSON strings if needed
    const authors = typeof this.authorsValue === 'string' 
      ? JSON.parse(this.authorsValue) 
      : this.authorsValue
    const selected = typeof this.selectedValue === 'string'
      ? JSON.parse(this.selectedValue)
      : this.selectedValue

    this.authorsValue = authors
    this.selectedValue = selected

    this.selectedAuthors = this.selectedValue.map(ref => 
      this.authorsValue.find(a => a.value === ref)
    ).filter(Boolean)
    this.filteredAuthors = this.authorsValue
    this.isOpen = false
    
    this.render()
    this.renderDropdown()
    
    // Add click outside listener
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener('click', this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.boundClickOutside)
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen
    this.dropdownTarget.style.display = this.isOpen ? 'block' : 'none'
    if (this.isOpen) {
      const searchInput = this.dropdownTarget.querySelector('.author-search-input')
      if (searchInput) searchInput.focus()
    }
  }

  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.style.display = 'none'
  }

  search(event) {
    const query = event.target.value.toLowerCase()
    this.filteredAuthors = this.authorsValue.filter(author => {
      const searchable = author.label.toLowerCase()
      return searchable.includes(query) && !this.isSelected(author.value)
    })
    this.renderDropdown()
  }

  selectAuthor(event) {
    const value = event.currentTarget.dataset.value
    const author = this.authorsValue.find(a => a.value === value)
    
    if (author && !this.isSelected(value)) {
      this.selectedAuthors.push(author)
      this.updateInput()
      this.render()
      this.renderDropdown()
      // Clear search
      const searchInput = this.dropdownTarget.querySelector('.author-search-input')
      if (searchInput) searchInput.value = ''
      this.filteredAuthors = this.authorsValue.filter(a => !this.isSelected(a.value))
    }
  }

  removeAuthor(event) {
    const value = event.currentTarget.dataset.value
    
    // Prevent removing the creator
    if (this.isCreator(value)) {
      return
    }
    
    this.selectedAuthors = this.selectedAuthors.filter(a => a.value !== value)
    this.updateInput()
    this.render()
    this.renderDropdown()
    // Clear search
    const searchInput = this.dropdownTarget.querySelector('.author-search-input')
    if (searchInput) searchInput.value = ''
    this.filteredAuthors = this.authorsValue.filter(a => !this.isSelected(a.value))
  }

  isSelected(value) {
    return this.selectedAuthors.some(a => a.value === value)
  }

  updateInput() {
    const values = this.selectedAuthors.map(a => a.value)
    this.inputTarget.value = JSON.stringify(values)
  }

  render() {
    this.selectedListTarget.innerHTML = ''
    
    if (this.selectedAuthors.length === 0) {
      this.selectedListTarget.innerHTML = '<div class="author-multiselect-empty">No authors selected</div>'
      return
    }
    
    this.selectedAuthors.forEach(author => {
      const tag = document.createElement('div')
      tag.className = 'author-tag'
      // Check if this is the creator (User with matching ID from post's user_id)
      const isCreator = this.isCreator(author.value)
      
      if (isCreator) {
        tag.innerHTML = `
          <span class="author-tag-label">${author.label} (Creator)</span>
          <span class="author-tag-locked" title="Creator cannot be removed">🔒</span>
        `
        tag.classList.add('author-tag-creator')
      } else {
        tag.innerHTML = `
          <span class="author-tag-label">${author.label}</span>
          <button type="button" class="author-tag-remove" data-value="${author.value}" 
                  data-action="click->author-multiselect#removeAuthor">×</button>
        `
      }
      this.selectedListTarget.appendChild(tag)
    })
  }

  isCreator(authorValue) {
    // Extract user ID from the author value (format: "User:123")
    if (authorValue && authorValue.startsWith('User:')) {
      const userId = parseInt(authorValue.split(':')[1])
      const postUserId = this.element.dataset.postUserId
      return postUserId && parseInt(postUserId) === userId
    }
    return false
  }

  renderDropdown() {
    const dropdown = this.dropdownTarget
    
    // Initialize dropdown HTML if not already present
    if (dropdown.querySelector('.author-search-input') === null) {
      dropdown.innerHTML = `
        <input type="text"
               class="author-search-input" 
               placeholder="Search authors..." 
               data-action="input->author-multiselect#search keydown->author-multiselect#handleKeydown">
        <div class="author-dropdown-items"></div>
      `
    }
    
    const itemsContainer = dropdown.querySelector('.author-dropdown-items')
    this.renderItems(itemsContainer)
  }

  renderItems(container) {
    container.innerHTML = ''
    
    const available = this.filteredAuthors.filter(a => !this.isSelected(a.value))
    
    if (available.length === 0) {
      container.innerHTML = '<div class="author-dropdown-empty">No authors available</div>'
      return
    }
    
    available.forEach(author => {
      const item = document.createElement('div')
      item.className = 'author-dropdown-item'
      item.dataset.value = author.value
      
      // Check if this is the creator
      if (this.isCreator(author.value)) {
        item.classList.add('author-dropdown-creator')
        item.innerHTML = `<span>${author.label} (Creator - Always included)</span>`
      } else {
        item.innerHTML = `<span>${author.label}</span>`
      }
      
      item.addEventListener('click', (e) => this.selectAuthor(e))
      container.appendChild(item)
    })
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDropdown()
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }
}

